// @ts-ignore TS7016 — declaration exists in packages/tsconfig/global.d.ts but is not loaded by all downstream tsconfigs
// TODO(noImplicitAny)
import { DateTime } from 'luxon-business-days';

/**
 * Federal observed-date rule: if a fixed-date holiday falls on Saturday,
 * it is observed on the prior Friday; if it falls on Sunday, it is
 * observed on the following Monday.
 */
const getObservedDate = (holiday: DateTime): DateTime => {
  if (holiday.weekday === 6) return holiday.minus({ days: 1 }); // Saturday -> Friday
  if (holiday.weekday === 7) return holiday.plus({ days: 1 }); // Sunday -> Monday
  return holiday;
};

const isFederalObservedHoliday = (inst: DateTime, month: number, day: number): boolean => {
  // Check both current and next year to handle year-boundary shifts
  // (e.g., Jan 1 on Saturday is observed Dec 31 of the prior year)
  for (const year of [inst.year, inst.year + 1]) {
    const observed = getObservedDate(DateTime.fromObject({ year, month, day }));
    if (inst.month === observed.month && inst.day === observed.day && inst.year === observed.year) {
      return true;
    }
  }
  return false;
};

/** New Year's Day: January 1 (observed) */
const isNewYearsDay = (inst: DateTime): boolean => isFederalObservedHoliday(inst, 1, 1);

/** Presidents' Day: Third Monday in February */
const isPresidentsDay = (inst: DateTime): boolean => {
  if (inst.month !== 2) return false;
  return inst.weekday === 1 && inst.day >= 15 && inst.day <= 21;
};

/** Juneteenth National Independence Day: June 19 (observed) */
const isJuneteenth = (inst: DateTime): boolean => isFederalObservedHoliday(inst, 6, 19);

/** Independence Day: July 4 (observed) */
const isIndependenceDay = (inst: DateTime): boolean => isFederalObservedHoliday(inst, 7, 4);

/** Veterans Day: November 11 (observed) */
const isVeteransDay = (inst: DateTime): boolean => isFederalObservedHoliday(inst, 11, 11);

/** Christmas Day: December 25 (observed) */
const isChristmasDay = (inst: DateTime): boolean => isFederalObservedHoliday(inst, 12, 25);

const setupFederalHolidays = (): void => {
  const dt = DateTime.local();
  dt.setupBusiness({
    holidayMatchers: [
      isNewYearsDay,
      dt.availableHolidayMatchers.isMLKDay,
      isPresidentsDay,
      dt.availableHolidayMatchers.isMemorialDay,
      isJuneteenth,
      isIndependenceDay,
      dt.availableHolidayMatchers.isLaborDay,
      dt.availableHolidayMatchers.isColumbusDay,
      isVeteransDay,
      dt.availableHolidayMatchers.isThanksgivingDay,
      isChristmasDay,
    ],
  });
};

export const addBusinessDaysToDateTime = (date: Date, days: number): DateTime => {
  setupFederalHolidays();
  return DateTime.fromJSDate(date).plusBusiness({ days });
};

export const addBusinessDays = (date: Date, days: number): Date => {
  return addBusinessDaysToDateTime(date, days).toJSDate();
};

export const isBusinessDay = (date: Date): boolean => {
  setupFederalHolidays();
  const dt = DateTime.fromJSDate(date).toUTC();
  return dt.isBusinessDay() && !dt.isHoliday();
};
